"use client";

/**
 * Compatibility layer for the App Router migration.
 *
 * Exposes the same surface the codebase used from `next/router`
 * (query, asPath, pathname, push, replace, back, prefetch, reload, isReady)
 * but implemented on top of `next/navigation`.
 *
 * `router.events` is intentionally a no-op emitter — the only consumer
 * was the old _app.tsx, which no longer exists.
 */

import {
  useParams,
  usePathname,
  useRouter as useNextRouter,
  useSearchParams,
} from "next/navigation";
import { useMemo } from "react";

type Query = Record<string, string | string[]>;

type Url = string | { pathname: string; query?: Record<string, any> };

export interface CompatRouter {
  query: Query;
  pathname: string;
  asPath: string;
  isReady: boolean;
  push: (url: Url, as?: unknown, options?: unknown) => Promise<boolean>;
  replace: (url: Url, as?: unknown, options?: unknown) => Promise<boolean>;
  back: () => void;
  prefetch: (url: string) => void;
  reload: () => void;
  events: {
    on: (...args: any[]) => void;
    off: (...args: any[]) => void;
    emit: (...args: any[]) => void;
  };
}

const toHref = (url: Url): string => {
  if (typeof url === "string") return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(url.query || {})) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => params.append(key, String(v)));
    } else {
      params.set(key, String(value));
    }
  }

  const search = params.toString();
  return search ? `${url.pathname}?${search}` : url.pathname;
};

const noopEvents = {
  on: () => {},
  off: () => {},
  emit: () => {},
};

export function useRouter(): CompatRouter {
  const router = useNextRouter();
  const pathname = usePathname() || "/";
  const searchParams = useSearchParams();
  const params = useParams();

  return useMemo<CompatRouter>(() => {
    const query: Query = {};

    // search params first
    searchParams?.forEach((value, key) => {
      const existing = query[key];
      if (existing === undefined) {
        query[key] = value;
      } else if (Array.isArray(existing)) {
        existing.push(value);
      } else {
        query[key] = [existing, value];
      }
    });

    // dynamic route params override (same precedence as pages router)
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) query[key] = value as string | string[];
      }
    }

    const search = searchParams?.toString();
    const asPath = search ? `${pathname}?${search}` : pathname;

    return {
      query,
      pathname,
      asPath,
      isReady: true,
      push: (url: Url) => {
        router.push(toHref(url));
        return Promise.resolve(true);
      },
      replace: (url: Url) => {
        router.replace(toHref(url));
        return Promise.resolve(true);
      },
      back: () => router.back(),
      prefetch: (url: string) => router.prefetch(url),
      reload: () => window.location.reload(),
      events: noopEvents,
    };
  }, [router, pathname, searchParams, params]);
}
